import React, { Fragment } from 'react';
import { isEmpty, isNil, isNumber, has, noop, isNaN, isUndefined } from 'lodash';
import { InputInteger } from 'src/common-ui';
import {
  ConfigurableDataSectionHeaderProps,
  ConfigurableDataSectionRowCellProps,
  ConfigurableDataSectionTotalRowProps,
  ConfigurableDataSectionRowProps,
  ConfigurableDataSectionRowsProps,
  ConfigurableDataSectionProps,
  ConfigurableDataModalTabsProps,
  ConfigurableDataColumn,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Editors/ConfigurableDataModal/ConfigurableDataModal.types';
import { isCellEditable } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Editors/ConfigurableDataModal/ConfigurableDataModal.utils';
import { FINAL_QTY } from 'src/utils/Domain/Constants';
import Renderer, { RendererOptions } from 'src/utils/Domain/Renderer';
import styles from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Editors/ConfigurableDataModal/ConfigurableDataModal.styles';

import coalesce from 'src/utils/Functions/Coalesce';
import { isInvalidNumber } from 'src/pages/AssortmentStrategy/TargetSetting/TargetSetting/TargetSetting.utils';
import { Tabs } from '@material-ui/core';
import * as globalMath from 'mathjs';

interface RenderCellParams {
  column: ConfigurableDataColumn;
  value: string | number | null;
  editable: boolean;
  highlight: boolean;
  rowHeaderValue?: string;
  onCellEdit: (rowDataIndexValue: string, colDataIndex: string, newValue: number) => void;
}

export function renderCellContent({
  column,
  value,
  editable,
  highlight,
  rowHeaderValue,
  onCellEdit,
}: RenderCellParams): React.ReactNode {
  if (editable && !isNil(rowHeaderValue)) {
    return (
      <div className={styles.tableCellInput}>
        <InputInteger
          onChange={(val) => {
            const parsedValue = isNumber(val) ? val : parseInt(val || '');
            onCellEdit(rowHeaderValue, column.dataIndex, parsedValue);
          }}
          editable
          valid
          value={value}
          nullable
        />
      </div>
    );
  }
  // don't display NaN values
  let displayVal = isNil(value) ? '' : String(value);
  if (isNaN(value) || displayVal.toLowerCase() === 'nan') {
    displayVal = '0';
  }

  if (!isNil(column.renderer) && !isNil(Renderer[column.renderer as RendererOptions]) && !isEmpty(displayVal)) {
    // @ts-expect-error
    displayVal = Renderer[column.renderer as RendererOptions](displayVal);
  }

  // If highlight + final_qty => style it as before
  const style = highlight && column.dataIndex === FINAL_QTY ? { color: '#f00' } : undefined;
  return <div style={style}>{displayVal}</div>;
}

const ConfigurableDataSectionHeader = ({ headers }: ConfigurableDataSectionHeaderProps) => {
  return (
    <div className={styles.tableRow}>
      {headers.map((header, index) => {
        const className = index > 0 ? styles.tableCellHeader : styles.tableCellHeaderFirst;
        return (
          <div className={className} key={`${header}-${index}`}>
            {header}
          </div>
        );
      })}
    </div>
  );
};

const ConfigurableDataSectionRowCell = ({
  cellConfig,
  cellClassName,
  cellValue,
  highlight,
  onCellEdit,
}: ConfigurableDataSectionRowCellProps) => {
  const { rowHeaderDataIndexValue, editable, column } = cellConfig;

  // Render the desired content via renderCellContent:
  const content = renderCellContent({
    column,
    value: cellValue,
    editable,
    highlight,
    rowHeaderValue: rowHeaderDataIndexValue,
    onCellEdit,
  });

  return <div className={cellClassName}>{content}</div>;
};

interface CreateCalculationParams {
  calculation: string;
  rowData: { [s: string]: number | string | null | boolean };
}

const createCalculation = ({ calculation, rowData }: CreateCalculationParams) => {
  const calc = globalMath.parse(calculation);
  if (rowData == null) {
    return '';
  }
  const vars: { [s: string]: number | string | null | boolean | undefined } = {};
  calc
    .filter((node) => node.isSymbolNode)
    .forEach((node) => {
      const id = node.name || '';
      if (!has(vars, id) && !has(globalMath, id)) {
        // FIXME: Right now, we use $ to represent `:` in var names as `:` is the range
        // operator. This is where we hotswap back. Yes...I know it's stupid. But so is
        // our `attribute:<name>:name` stuff so...vOv
        const key = id.replace(/\$/g, ':');
        vars[id] = coalesce(rowData[key], '');
      }
    });
  let result;
  try {
    result = globalMath.evaluate(calculation, vars);
  } catch (e) {
    // TODO: Mark has been naughty here...
    noop();
  }
  if (isInvalidNumber(result)) {
    result = 0;
  }
  return result;
};

// TODO: see about combining component with regular DataSectionRow below, very minor differences I believe
const ConfigurableDataSectionTotalRow = ({
  sectionConfig,
  totalRowData,
  highlight,
}: ConfigurableDataSectionTotalRowProps) => {
  const { modalEditsEnabled, dataRules, columns, onCellEdit } = sectionConfig;
  return (
    <div className={styles.tableRow}>
      {columns.map((column, index) => {
        const { dataIndex, editable, editableByCalc, calculation } = column;
        const className = index > 0 ? styles.tableCellBody : styles.tableCellBodyFirst;

        let value: string | number | null;
        if (calculation) {
          value = createCalculation({ calculation, rowData: totalRowData });
        } else {
          value = index > 0 ? totalRowData[dataIndex] : 'Total';
        }
        const isEditable = !modalEditsEnabled
          ? false
          : isCellEditable({ dataIndex, editable, editableByCalc }, dataRules, totalRowData);

        return (
          <ConfigurableDataSectionRowCell
            key={`${dataIndex}-${index}`}
            cellConfig={{ rowHeaderDataIndexValue: 'Total', dataIndex, editable: isEditable, column }}
            cellClassName={className}
            cellValue={value}
            highlight={highlight}
            onCellEdit={onCellEdit}
          />
        );
      })}
    </div>
  );
};

const ConfigurableDataSectionRow = ({
  sectionConfig,
  rowData,
  highlight,
  totalRowData,
}: ConfigurableDataSectionRowProps) => {
  if (isEmpty(rowData)) {
    return <div className={styles.tableRow} />;
  }

  const { modalEditsEnabled, dataRules, columns, rowHeaderDataIndex, onCellEdit } = sectionConfig;
  return (
    <Fragment>
      {rowData.map((data, rowIndex) => {
        return (
          <div className={styles.tableRow} key={`row-${rowIndex}`}>
            {columns.map((column, colIndex) => {
              const { dataIndex, editable, editableByCalc, calculation } = column;
              const className = colIndex > 0 ? styles.tableCellBody : styles.tableCellBodyFirst;

              let value: string | number | null;
              if (calculation) {
                value = createCalculation({ calculation, rowData: data });
              } else {
                // If first column lacks a dataIndex, attempt to use the rowHeaderDataIndex (config.dataSectionHeaders.bottomRowsDataIndex)
                // This is here to maintain previous behavior without creating too much jank. -MrT
                if (colIndex === 0 && isEmpty(dataIndex) && !isNil(data[rowHeaderDataIndex])) {
                  value = data[rowHeaderDataIndex];
                } else {
                  value = data[dataIndex];
                }
              }

              const isEditable = modalEditsEnabled
                ? isCellEditable({ dataIndex, editable, editableByCalc }, dataRules, totalRowData)
                : false;

              return (
                <ConfigurableDataSectionRowCell
                  key={`${dataIndex}-${rowIndex}`}
                  cellConfig={{
                    rowHeaderDataIndexValue: data[rowHeaderDataIndex] as string,
                    dataIndex,
                    editable: isEditable,
                    column,
                  }}
                  cellClassName={className}
                  cellValue={value}
                  highlight={highlight}
                  onCellEdit={onCellEdit}
                />
              );
            })}
          </div>
        );
      })}
    </Fragment>
  );
};

const ConfigurableDataSectionRows = ({
  renderSizeRowData,
  rowData,
  totalRowData,
  sectionConfig,
  highlight,
}: ConfigurableDataSectionRowsProps) => {
  return (
    <Fragment>
      {renderSizeRowData ? (
        <ConfigurableDataSectionRow
          sectionConfig={sectionConfig}
          rowData={rowData}
          highlight={highlight}
          totalRowData={totalRowData}
        />
      ) : (
        <ConfigurableDataSectionTotalRow
          sectionConfig={sectionConfig}
          totalRowData={totalRowData}
          highlight={highlight}
        />
      )}
    </Fragment>
  );
};

export const ConfigurableDataSection = ({
  sectionHeader,
  sectionConfig,
  totalRowData,
  rowData,
  highlight,
}: ConfigurableDataSectionProps) => {
  const { columns } = sectionConfig;
  const headers = columns.map((c) => c.text);

  return (
    <div className={styles.receiptContainer}>
      <div className={styles.headerSection}>
        <span className={styles.sectionText}>{sectionHeader}</span>
      </div>
      <div className={styles.tableSection}>
        <ConfigurableDataSectionHeader headers={headers} />
        <ConfigurableDataSectionRows
          renderSizeRowData={!isNil(rowData)}
          rowData={!isNil(rowData) ? rowData : []}
          totalRowData={totalRowData}
          sectionConfig={sectionConfig}
          highlight={highlight}
        />
      </div>
    </div>
  );
};

export const ConfigurableDataModalTabs = ({ tabs, activeTabIndex, onTabChange }: ConfigurableDataModalTabsProps) => {
  return (
    <Tabs
      value={activeTabIndex}
      variant="scrollable"
      scrollButtons="on"
      aria-label="scrollable auto tabs"
      style={{ minHeight: 30 }}
      className={styles.tabContainer}
    >
      {tabs.map((tab, index) => {
        const tabClass = activeTabIndex === index ? styles.tabButtonActive : styles.tabButton;
        const handleTabChange = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          event.stopPropagation();
          onTabChange(index);
        };
        return (
          <button className={tabClass} onClick={handleTabChange} key={`${tab}-${index}`}>
            {tab}
          </button>
        );
      })}
    </Tabs>
  );
};
